import { useEffect, useId, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { AnswerRating, FlagReason } from '@/lib/agent/answerFeedback'
import { cn } from '@/utils/cn'

const COMMENT_MAX_LENGTH = 1000

const FLAG_REASONS: { value: FlagReason; label: string }[] = [
  { value: FlagReason.WrongFact, label: 'Wrong fact' },
  { value: FlagReason.WrongAction, label: 'Wrong action' },
  { value: FlagReason.DataInWrongPlace, label: 'Data in wrong place' },
  { value: FlagReason.Other, label: 'Other' },
]

const PROMPT_BY_RATING: Record<AnswerRating, string> = {
  [AnswerRating.Up]: 'Add a comment',
  [AnswerRating.Down]: 'What was wrong?',
  [AnswerRating.Flag]: 'Why is this answer harmful?',
}

export interface CommentDraft {
  rating: AnswerRating
  flagReason: FlagReason | ''
  comment: string
}

/** The comment box under a rated answer; a flag also needs one of the fixed reasons before it saves. */
export function AnswerCommentBox({
  draft,
  onChange,
  onCancel,
  onSave,
  saving,
}: {
  draft: CommentDraft
  onChange: (draft: CommentDraft) => void
  onCancel: () => void
  onSave: () => void
  saving: boolean
}) {
  const commentId = useId()
  const boxRef = useRef<HTMLDivElement>(null)
  const isFlag = draft.rating === AnswerRating.Flag
  // The box opens under the last answer, often below the chat's fold on a phone.
  useEffect(() => {
    boxRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [])
  return (
    <div ref={boxRef} className="bg-card space-y-2 rounded-lg border p-3">
      <Label htmlFor={commentId}>{PROMPT_BY_RATING[draft.rating]}</Label>
      {isFlag && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Flag reason">
          {FLAG_REASONS.map((reason) => {
            const selected = draft.flagReason === reason.value
            return (
              <Button
                key={reason.value}
                variant="outlineThin"
                size="xs"
                aria-pressed={selected}
                className={cn(selected && 'border-brand-600 bg-brand-100 hover:bg-brand-100')}
                onClick={() => onChange({ ...draft, flagReason: reason.value })}
              >
                {reason.label}
              </Button>
            )
          })}
        </div>
      )}
      <Textarea
        id={commentId}
        placeholder="What should Cai have said? (optional)"
        maxLength={COMMENT_MAX_LENGTH}
        value={draft.comment}
        onChange={(event) => onChange({ ...draft, comment: event.target.value })}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs whitespace-nowrap">
          {draft.comment.length} / {COMMENT_MAX_LENGTH.toLocaleString()}
        </span>
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={isFlag && !draft.flagReason}
            loading={saving ? 'Saving…' : undefined}
            onClick={onSave}
          >
            {isFlag ? 'Flag answer' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}
